import { ROLES, type UserType } from './roles';
import { fieldsFor, stepsFor, type Badge, type Field, type Step } from './steps';

export type AnswerValue = string | string[];
export type Answers = Record<string, AnswerValue>;

/** Rank ladder cut points, as completion percentages. Five ranks, five gates. */
const RANK_GATES = [0, 25, 50, 75, 95] as const;

export function isAnswered(field: Field, answers: Answers): boolean {
  const value = answers[field.key];
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'string' && value.trim().length > 0;
}

export function stepComplete(step: Step, answers: Answers): boolean {
  return step.fields.every(field => field.optional || isAnswered(field, answers));
}

export function earnedXp(userType: UserType, answers: Answers): number {
  return fieldsFor(userType).reduce((total, field) => (isAnswered(field, answers) ? total + field.xp : total), 0);
}

export function maxXp(userType: UserType): number {
  return fieldsFor(userType).reduce((total, field) => total + field.xp, 0);
}

/**
 * One number, computed from the answers alone — no hidden weighting, no server
 * opinion. Section 16 of the spec asks for explainable signals rather than a
 * mysterious score, and this is the smallest honest version of that.
 */
export function completion(userType: UserType, answers: Answers): number {
  const max = maxXp(userType);
  if (max === 0) return 0;
  return Math.round((earnedXp(userType, answers) / max) * 100);
}

export interface Rank {
  index: number;
  label: string;
  /** Null at the top of the ladder. */
  next: string | null;
  /** Completion points still needed for `next`. Zero at the top. */
  toNext: number;
}

export function rankOf(userType: UserType, percent: number): Rank {
  const ladder = ROLES[userType].ranks;
  let index = 0;
  for (let i = 0; i < RANK_GATES.length; i += 1) if (percent >= RANK_GATES[i]) index = i;
  const next = index < ladder.length - 1 ? ladder[index + 1] : null;
  return {
    index,
    label: ladder[index],
    next,
    toNext: next ? Math.max(0, RANK_GATES[index + 1] - percent) : 0
  };
}

export function badgesFor(userType: UserType, answers: Answers): Badge[] {
  return stepsFor(userType)
    .filter(step => stepComplete(step, answers))
    .map(step => step.badge);
}

export interface NextAction {
  step: Step;
  stepIndex: number;
  field: Field;
}

/**
 * The single most valuable unanswered question — highest XP first, earliest step
 * as the tiebreak. The dashboard leads with this so "finish your profile" is
 * always one concrete thing rather than a chore list.
 */
export function nextAction(userType: UserType, answers: Answers): NextAction | null {
  let best: NextAction | null = null;
  stepsFor(userType).forEach((step, stepIndex) => {
    step.fields.forEach(field => {
      if (isAnswered(field, answers)) return;
      if (!best || field.xp > best.field.xp) best = { step, stepIndex, field };
    });
  });
  return best;
}

/** Membership number, zero-padded the way a crew badge would print it. */
export function crewNumber(memberNo: number | null): string {
  if (!memberNo || memberNo < 1) return '————';
  return `#${String(memberNo).padStart(4, '0')}`;
}

/** The one line under the name on the crew card. Each role calls it something different. */
export function disciplineOf(userType: UserType, answers: Answers): string {
  const first = (key: string): string => {
    const value = answers[key];
    if (Array.isArray(value)) return value[0] || '';
    return typeof value === 'string' ? value : '';
  };

  const pick =
    userType === 'professional'
      ? first('primary_role') || first('specializations')
      : userType === 'company'
        ? first('needs') || first('industry')
        : userType === 'student'
          ? first('field_of_study') || first('tracks')
          : first('make_interests') || first('tracks');

  return pick || 'Not set yet';
}
